﻿import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import {
    str_isEmpty,
    get_total_working_hours
} from '@Util/Utils';

import { H2, P } from '@Elements/Fonts';
import Content_Container from '@Elements/Content_Container';

import { Profile_Presentation_Props, Time_Log_Entry } from '@Util/Types';

export default function Profile_Presentation(props: Profile_Presentation_Props): JSX.Element {
    const [t] = useTranslation();

    // CALCULATES TOTAL WORKING HOURS FOR TODAY
    const todays_active_hours = useMemo(() => {
        return {
            detailed: get_total_working_hours(props.todays_time_log, "asDetailed"),
            overview: get_total_working_hours(props.todays_time_log, "asHours")
        }
    }, [props.todays_time_log]);

    // CALCULATES TOTAL WORKING HOURS FOR THIS WEEK
    const weeks_active_hours = useMemo(() => {
        return {
            detailed: get_total_working_hours(props.weeks_time_log, "asDetailed"),
            overview: get_total_working_hours(props.weeks_time_log, "asHours")
        }
    }, [props.weeks_time_log]);

    const calculate_break_time = (time_logs: Time_Log_Entry[] | undefined | null): string => {
        // If timeLogs is undefined or null, return "0 minutes"
        if (!time_logs) {
            return "0 minutes";
        }
    
        // Sort the time logs by startDate in ascending order
        const sorted_time_logs = [...time_logs].sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());
    
        let break_time = 0;
    
        // Loop through the sorted array from the second element
        for (let i = 1; i < sorted_time_logs.length; i++) {
            // Calculate the difference in minutes between the startDate of the current element and the endDate of the previous one
            const diff = dayjs(new Date(sorted_time_logs[i].startDate)).diff(dayjs(new Date(sorted_time_logs[i - 1].endDate)), 'minute');
    
            // If the difference is greater than 0, add it to breakTime
            if (diff > 0) {
                break_time += diff;
            }
        }
    
        // Return the total break time in minutes as a string
        return break_time === 1 ? `${break_time} ${t('Text.Minute')}` : `${break_time} ${t('Text.Minutes')}`;
    };
    
    const todays_break_time = useMemo(() => calculate_break_time(props.todays_time_log || []), [props.todays_time_log]);

    return (
        <section className="Module">
            <header className="Module_Header">
                <H2>{t('Text.User')}</H2>
            </header>
            <Content_Container>
                <div className="Profile_Presentation">
                    <div className="Profile_Presentation_Image">
                        <div className="Profile_Presentation_Image_Wrapper" style={{ borderColor: "rgb(" + props.current_project?.color + ")" }}>
                            {
                                !str_isEmpty(props.logged_in_user.photo.fileUrl) ?
                                    <img
                                        src={props.logged_in_user.photo.fileUrl}
                                        alt={props.logged_in_user.photo.fileName}
                                        loading="eager"
                                    /> :
                                    <div className="Profile_Presentation_Image_Default">
                                        <svg xmlns="http://www.w3.org/2000/svg" height="48" width="48" viewBox="0 0 48 48"><path d="M11.1 35.25q3.15-2 6.225-3.025Q20.4 31.2 24 31.2q3.6 0 6.7 1.025t6.25 3.025q2.2-2.7 3.125-5.45Q41 27.05 41 24q0-7.25-4.875-12.125T24 7q-7.25 0-12.125 4.875T7 24q0 3.05.95 5.8t3.15 5.45ZM24 25.5q-2.9 0-4.875-1.975T17.15 18.65q0-2.9 1.975-4.875T24 11.8q2.9 0 4.875 1.975t1.975 4.875q0 2.9-1.975 4.875T24 25.5ZM24 44q-4.2 0-7.85-1.575-3.65-1.575-6.35-4.3Q7.1 35.4 5.55 31.75 4 28.1 4 23.95q0-4.1 1.575-7.75 1.575-3.65 4.3-6.35 2.725-2.7 6.375-4.275Q19.9 4 24.05 4q4.1 0 7.75 1.575 3.65 1.575 6.35 4.275 2.7 2.7 4.275 6.35Q44 19.85 44 24q0 4.1-1.575 7.75-1.575 3.65-4.275 6.375t-6.35 4.3Q28.15 44 24 44Z" /></svg>
                                    </div>
                            }
                        </div>
                    </div>
                    <div className="Profile_Presentation_Text">
                        <h3 className="Headline2">{`${props.logged_in_user.firstName} ${props.logged_in_user.lastName}`}</h3>
                        <div className="Profile_Presentation_Text_Row">
                            <P>
                                <strong>{t('Text.WorkedToday')}:</strong>
                            </P>
                            <P secondary={true}>{todays_active_hours.detailed}</P>
                        </div>
                        <div className="Profile_Presentation_Text_Row">
                            <P>
                                <strong>{t('Text.WorkedThisWeek')}:</strong>
                            </P>
                            <P secondary={true}>{weeks_active_hours.detailed}</P>
                        </div>
                        <div className="Profile_Presentation_Text_Row">
                            <P>
                                <strong>{t('Text.PauseToday')}:</strong>
                            </P>
                            <P secondary={true}>{todays_break_time}</P>
                        </div>
                    </div>
                </div>
            </Content_Container>
        </section>
    )
}